import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { Copy, TrashIcon, X } from 'lucide-react';

import { closeModal } from '@/components/modals/modals.utils';
import { ModalBase } from '@/components/modals/modals-base';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { env } from '@/constants/env';
import {
  getVirtualTourByIdOptions,
  useRevokeToken,
  useShareVirtualTour,
} from '@/features/estate-media/api/virtual-tour';
import { queryClient } from '@/lib/react-query';

dayjs.extend(localizedFormat);

export interface ShareVirtualTourModalProps {
  estateId: string;
  tourId: string;
  presignedUrls?: {
    token: string;
    expiryDate: string;
  }[];
}

export const ShareVirtoulTourModal: React.FC<ShareVirtualTourModalProps> = ({
  estateId,
  tourId,
  presignedUrls,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [expiresIn, setExpiresIn] = useState<number>(60);
  // necessary to correctly remove a deleted link without closing the modal
  const [displayedLinks, setDisplayedLinks] = useState(presignedUrls);
  const { t } = useTranslation();

  const { mutate: shareVirtualTour } = useShareVirtualTour({
    mutationConfig: {
      onSuccess: ([{ token, expiryDate }]) => {
        const url = `${env.PUBLIC_EDITOR_URL}/view?token=${token}`;
        navigator.clipboard.writeText(url);
        toast.success(t('copied-url'));
        setDisplayedLinks((prev) => [...(prev ?? []), { token, expiryDate }]);
        setIsDropdownOpen(false);
        queryClient.invalidateQueries({
          queryKey: getVirtualTourByIdOptions(estateId, tourId).queryKey,
        });
      },
      onError: (err) => {
        toast.error(err.message);
      },
    },
  });

  const { mutate: revokeToken } = useRevokeToken({
    mutationConfig: {
      onSuccess: ([{ token }]) => {
        toast.success(t('successfully-revoked-link'));
        setDisplayedLinks(displayedLinks && displayedLinks.filter((l) => l.token !== token));
        queryClient.invalidateQueries({
          queryKey: getVirtualTourByIdOptions(estateId, tourId).queryKey,
        });
      },
      onError: (err) => {
        toast.error(err.message);
      },
    },
  });

  const handleRevoke = (token: string) => {
    revokeToken({ token });
  };

  const createNewPresignedUrl = () => {
    if (!estateId) return;
    shareVirtualTour({
      estateId: estateId,
      virtualTourId: tourId,
      expiresIn: expiresIn,
    });
  };

  return (
    <ModalBase className="w-[40vw]">
      <X className="ml-auto cursor-pointer stroke-[4px] text-primary-light" onClick={closeModal} />
      <section className="mt-4 flex flex-row justify-between">
        <h3 className="text-xl font-semibold">Links</h3>
        <DropdownMenu open={isDropdownOpen}>
          <DropdownMenuTrigger onClick={() => setIsDropdownOpen(true)} asChild>
            <Button>Create new link</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="px-3 py-4">
            <div className="flex flex-col">
              <Input
                label={t('expires-in')}
                value={expiresIn}
                type="number"
                onChange={(e) => setExpiresIn(parseInt(e.target.value))}
              />
            </div>
            <div className="mt-4 flex flex-row gap-2">
              <Button variant="contained" onClick={createNewPresignedUrl}>
                {t('share')}
              </Button>
              <Button variant="outline" onClick={() => setIsDropdownOpen(false)}>
                {t('close')}
              </Button>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </section>
      {displayedLinks && displayedLinks.length > 0 ? (
        <Table className="my-5 w-full table-auto bg-transparent">
          <TableHeader className="h-11 max-sm:hidden">
            <TableRow className="w-full justify-between">
              <TableHead className="text-center text-text-secondary">Link</TableHead>
              <TableHead className="text-center text-text-secondary">{t('expires-at')}</TableHead>
              <TableHead className="text-center text-text-secondary"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayedLinks.map((presignedUrl) => {
              const url = `/shared/virtualTour/${estateId}/${tourId}?token=${presignedUrl.token}`;

              if (dayjs().isAfter(presignedUrl.expiryDate)) return;

              return (
                <TableRow key={presignedUrl.token} className="items-center text-primary-dark">
                  <TableCell className="max-w-40 truncate py-5 text-center">
                    <a className="no-underline" href={url} target="__blank">
                      {url}
                    </a>
                  </TableCell>
                  <TableCell className="max-w-40 whitespace-normal break-words py-5 text-center lg:w-1/4">
                    {dayjs(presignedUrl.expiryDate).format('L')}
                  </TableCell>
                  <TableCell>
                    <div className="hidden w-auto items-center justify-end gap-1 py-5 text-center lg:grid lg:grid-cols-2 xl:flex">
                      <Button
                        onClick={() => {
                          navigator.clipboard.writeText(url);
                          toast.success(t('copied-url'));
                        }}
                        variant="outline"
                        className="border-2"
                      >
                        <Copy aria-hidden />
                        <span className="sr-only">{t('copy')}</span>
                      </Button>
                      <Button
                        className="max-xl:max-w-[60px]"
                        onClick={() => handleRevoke(presignedUrl.token)}
                        variant="destructive"
                      >
                        <TrashIcon aria-hidden />
                        <span className="sr-only"> {t('delete')}</span>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      ) : (
        <div className="flex justify-center py-16">
          <span>{t('noDocuments')}</span>
        </div>
      )}
    </ModalBase>
  );
};
